import { useState, useCallback, useRef } from 'react'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import { Upload, X, FileText, AlertCircle, CheckCircle2 } from 'lucide-react'
import {
  validateFile,
  formatFileSize,
  ALLOWED_FILE_TYPES,
  MAX_FILE_SIZE_MB,
  type DocumentUploadResponse,
} from '@/schemas/document'
import { documentService } from '@/services/document'

interface FileItem {
  file: File
  error: string | null
  progress: number
  uploaded: boolean
}

interface DocumentUploadZoneProps {
  projectId: string
  kbId: string
  onUploadComplete?: (response: DocumentUploadResponse) => void
  onError?: (error: Error) => void
}

export function DocumentUploadZone({
  projectId,
  kbId,
  onUploadComplete,
  onError,
}: DocumentUploadZoneProps) {
  const [isDragging, setIsDragging] = useState(false)
  const [files, setFiles] = useState<FileItem[]>([])
  const [isUploading, setIsUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }, [])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const addFiles = useCallback((newFiles: FileList | File[]) => {
    const fileArray = Array.from(newFiles)
    const newFileItems: FileItem[] = fileArray.map((file) => ({
      file,
      error: validateFile(file),
      progress: 0,
      uploaded: false,
    }))
    setFiles((prev) => [...prev, ...newFileItems])
  }, [])

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      e.stopPropagation()
      setIsDragging(false)

      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        addFiles(e.dataTransfer.files)
      }
    },
    [addFiles]
  )

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        addFiles(e.target.files)
      }
      // Reset input value to allow selecting the same file again
      if (inputRef.current) {
        inputRef.current.value = ''
      }
    },
    [addFiles]
  )

  const removeFile = useCallback((index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index))
  }, [])

  const clearFiles = useCallback(() => {
    setFiles([])
    setUploadProgress(0)
  }, [])

  const handleUpload = async () => {
    // Filter valid files only
    const validFiles = files.filter((f) => !f.error).map((f) => f.file)
    if (validFiles.length === 0) return

    setIsUploading(true)
    setUploadProgress(0)

    try {
      const response = await documentService.uploadDocuments(
        projectId,
        kbId,
        validFiles,
        (progress) => {
          setUploadProgress(progress)
        }
      )

      // Mark files as uploaded
      setFiles((prev) =>
        prev.map((f) => ({
          ...f,
          uploaded: !f.error,
        }))
      )

      onUploadComplete?.(response)

      // Clear files after successful upload
      setTimeout(() => {
        clearFiles()
      }, 2000)
    } catch (err) {
      onError?.(err instanceof Error ? err : new Error('Erro ao fazer upload'))
    } finally {
      setIsUploading(false)
    }
  }

  const validFilesCount = files.filter((f) => !f.error).length
  const hasFiles = files.length > 0
  const canUpload = validFilesCount > 0 && !isUploading

  // Accept string for input
  const acceptString = ALLOWED_FILE_TYPES.map((t) => `.${t}`).join(',')

  return (
    <div className="space-y-4">
      {/* Drop Zone */}
      <Card
        variant="glass"
        className={`
          p-8 border-2 border-dashed transition-all duration-200 cursor-pointer
          ${
            isDragging
              ? 'border-emerald-500 bg-emerald-500/10'
              : 'border-gray-600 hover:border-emerald-500/50 hover:bg-white/5'
          }
        `}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
      >
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={acceptString}
          className="hidden"
          onChange={handleFileSelect}
        />

        <div className="flex flex-col items-center justify-center text-center">
          <div
            className={`
              w-16 h-16 rounded-full flex items-center justify-center mb-4 transition-colors
              ${isDragging ? 'bg-emerald-500/20' : 'bg-gray-700/50'}
            `}
          >
            <Upload
              className={`w-8 h-8 ${isDragging ? 'text-emerald-400' : 'text-gray-400'}`}
            />
          </div>
          <h3 className="text-lg font-medium text-white mb-2">
            {isDragging ? 'Solte os arquivos aqui' : 'Arraste arquivos ou clique para selecionar'}
          </h3>
          <p className="text-sm text-gray-400">
            Formatos aceitos: {ALLOWED_FILE_TYPES.join(', ').toUpperCase()} | Max: {MAX_FILE_SIZE_MB}MB
          </p>
        </div>
      </Card>

      {/* File List */}
      {hasFiles && (
        <Card variant="glass" className="p-4">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-sm font-medium text-white">
              {files.length} {files.length === 1 ? 'arquivo selecionado' : 'arquivos selecionados'}
              {validFilesCount !== files.length && (
                <span className="text-gray-400 ml-2">
                  ({validFilesCount} {validFilesCount === 1 ? 'valido' : 'validos'})
                </span>
              )}
            </h4>
            <Button
              variant="ghost"
              size="sm"
              onClick={clearFiles}
              disabled={isUploading}
              className="text-gray-400 hover:text-white"
            >
              Limpar
            </Button>
          </div>

          {/* Upload Progress */}
          {isUploading && (
            <div className="mb-4">
              <div className="flex items-center justify-between text-sm mb-2">
                <span className="text-gray-400">Enviando...</span>
                <span className="text-emerald-400">{uploadProgress}%</span>
              </div>
              <Progress value={uploadProgress} className="h-2" />
            </div>
          )}

          {/* Files */}
          <div className="space-y-2 max-h-[300px] overflow-y-auto">
            {files.map((item, index) => (
              <div
                key={`${item.file.name}-${index}`}
                className={`
                  flex items-center gap-3 p-3 rounded-lg
                  ${item.error ? 'bg-red-500/10' : item.uploaded ? 'bg-emerald-500/10' : 'bg-white/5'}
                `}
              >
                {/* Icon */}
                <div
                  className={`
                    w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0
                    ${item.error ? 'bg-red-500/20' : item.uploaded ? 'bg-emerald-500/20' : 'bg-gray-700/50'}
                  `}
                >
                  {item.error ? (
                    <AlertCircle className="w-5 h-5 text-red-400" />
                  ) : item.uploaded ? (
                    <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                  ) : (
                    <FileText className="w-5 h-5 text-gray-400" />
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white truncate">{item.file.name}</p>
                  {item.error ? (
                    <p className="text-xs text-red-400">{item.error}</p>
                  ) : (
                    <p className="text-xs text-gray-400">{formatFileSize(item.file.size)}</p>
                  )}
                </div>

                {/* Remove Button */}
                {!isUploading && !item.uploaded && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-gray-400 hover:text-white flex-shrink-0"
                    onClick={() => removeFile(index)}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                )}
              </div>
            ))}
          </div>

          {/* Upload Button */}
          <div className="mt-4 flex justify-end">
            <Button
              onClick={handleUpload}
              disabled={!canUpload}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              <Upload className="w-4 h-4 mr-2" />
              Enviar {validFilesCount > 0 && `(${validFilesCount})`}
            </Button>
          </div>
        </Card>
      )}
    </div>
  )
}
